import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import AutocompleteWithPagination from "../../../components/autocomplete-with-pagination";
import { productService } from "../../../utils/services/productService";
import { supplierService } from "../../../utils/services/supplierService";
import { variantAttributeService } from "../../../utils/services/variantAttributeService";
import { FILTER_OPTIONS, fieldSx } from "../constants";
import FieldLabel from "./FieldLabel";

const ImportProductsModal = ({
  open,
  onClose,
  onImport,
  selectedClient,
  loading,
  type,
}) => {
  const [filterBy, setFilterBy] = useState("");
  const [filterItem, setFilterItem] = useState([]);
  const [attributeType, setAttributeType] = useState(null);
  const [attributeValue, setAttributeValue] = useState([]);
  const [importMode, setImportMode] = useState("concat");
  const [pagingMode, setPagingMode] = useState("itemsPerPage"); // 'itemsPerPage' | 'maxPages'
  const [perPage, setPerPage] = useState("");
  const [maxPages, setMaxPages] = useState("");
  const [statusOption, setStatusOption] = useState("both"); // 'active' | 'both'

  const handleClose = () => {
    setFilterBy("");
    setFilterItem([]);
    setAttributeType(null);
    setAttributeValue([]);
    setImportMode("concat");
    setPagingMode("itemsPerPage");
    setPerPage("");
    setMaxPages("");
    setStatusOption("both");
    onClose();
  };

  const handleImport = () => {
    const pp =
      pagingMode === "itemsPerPage" && perPage !== "" && Number(perPage) > 0
        ? Number(perPage)
        : 0;
    const mp =
      pagingMode === "maxPages" && maxPages !== "" && Number(maxPages) > 0
        ? Number(maxPages)
        : 0;
    if (filterBy === "attribute") {
      if (!attributeType || !attributeValue.length) return;
      onImport(
        filterBy,
        {
          attributeType: attributeType.option_type,
          attributeValues: attributeValue.map((v) => v.value),
        },
        importMode,
        pp,
        mp,
        statusOption === "active" ? "active" : null,
      );
    } else {
      if (!filterBy || !filterItem.length) return;
      onImport(
        filterBy,
        filterItem,
        importMode,
        pp,
        mp,
        statusOption === "active" ? "active" : null,
      );
    }
    handleClose();
  };

  const hasItems =
    filterItem?.length > 0 ||
    (filterBy === "attribute" && attributeType && attributeValue.length > 0);

  const pp =
    pagingMode === "itemsPerPage" && perPage !== "" && Number(perPage) > 0
      ? Number(perPage)
      : 0;
  const mp =
    pagingMode === "maxPages" && maxPages !== "" && Number(maxPages) > 0
      ? Number(maxPages)
      : 0;

  // Preview rows for items-per-page mode: show first 3 pages as example
  const previewRows =
    pp > 0
      ? [1, 2, 3].map((page) => ({
          page,
          from: (page - 1) * pp + 1,
          to: page * pp,
        }))
      : [];

  const autocompleteKey = filterBy; // remount when type changes

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle
        sx={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 15, pb: 1 }}
      >
        Import Products
      </DialogTitle>
      <Divider sx={{ borderColor: "var(--border-color)" }} />
      <DialogContent sx={{ pt: 2.5, pb: 1 }}>
        {/* Filter type selector */}
        <FormControl fullWidth size="small" sx={{ mb: 2.5 }}>
          <Typography
            fontFamily="Poppins"
            fontSize={13}
            fontWeight={500}
            color="var(--text-primary)"
            sx={{ mb: 0.75 }}
          >
            {`Select products by`}
          </Typography>

          <Select
            value={filterBy}
            // label="Select products by"
            onChange={(e) => {
              setFilterBy(e.target.value);
              setFilterItem([]);
              setAttributeType(null);
              setAttributeValue([]);
            }}
            sx={{
              fontFamily: "Poppins",
              fontSize: 13,
              borderRadius: "10px",
              backgroundColor: "var(--bg-paper)",
              color: "var(--text-primary)",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "var(--border-color)",
              },
            }}
          >
            {FILTER_OPTIONS.map((opt) => (
              <MenuItem
                key={opt.value}
                value={opt.value}
                sx={{ fontFamily: "Poppins", fontSize: 13 }}
              >
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Dynamic autocomplete/fields based on filter type */}
        {filterBy && filterBy !== "attribute" && (
          <Box>
            <Typography
              fontFamily="Poppins"
              fontSize={13}
              fontWeight={500}
              color="var(--text-primary)"
              sx={{ mb: 0.75 }}
            >
              {filterBy === "category" ? "Category" : "Supplier"}
            </Typography>
            <AutocompleteWithPagination
              multiple
              key={autocompleteKey}
              selectedItem={filterItem}
              onDataChange={(items) => setFilterItem(items || [])}
              selectedClient={selectedClient}
              width="100%"
              optionKey={filterBy === "category" ? "_id" : "name"}
              hideLabel
              apiEndPoint={(limit, skip, search, client) =>
                filterBy === "category"
                  ? productService.fetchCategory(search, client, limit, skip)
                  : supplierService.fetch(
                      search,
                      client,
                      `$limit=${limit}&$skip=${skip}`,
                      "name",
                    )
              }
              errorMessage={null}
            />
          </Box>
        )}

        {/* Attribute filter fields */}
        {filterBy === "attribute" && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box>
              <FieldLabel required>Attribute Type</FieldLabel>
              <AutocompleteWithPagination
                selectedItem={attributeType}
                onDataChange={(item) => {
                  setAttributeType(item || null);
                  setAttributeValue([]); // Reset values when type changes
                }}
                selectedClient={selectedClient}
                width="100%"
                optionKey="option_type"
                hideLabel
                apiEndPoint={(limit, skip, search, client) =>
                  variantAttributeService.fetchOptionTypes(
                    client,
                    search,
                    limit,
                    skip,
                  )
                }
                errorMessage={null}
              />
            </Box>

            {attributeType && (
              <Box>
                <FieldLabel required>Attribute Value</FieldLabel>
                <AutocompleteWithPagination
                  multiple
                  key={attributeType.option_type}
                  selectedItem={attributeValue}
                  onDataChange={(items) => setAttributeValue(items || [])}
                  selectedClient={selectedClient}
                  width="100%"
                  optionKey="value"
                  hideLabel
                  apiEndPoint={(limit, skip, search, client) =>
                    variantAttributeService.fetchOptionValues(
                      attributeType.option_type,
                      client,
                      search,
                      limit,
                      skip,
                    )
                  }
                  errorMessage={null}
                />
              </Box>
            )}
          </Box>
        )}

        {/* Products per page + preview — shown once an item is selected */}
        {hasItems && type !== "Online" && (
          <Box sx={{ mt: 2.5 }}>
            <Typography
              fontFamily="Poppins"
              fontSize={12}
              fontWeight={600}
              color="primary.main"
              sx={{ mb: 0.75 }}
            >
              ★ Page assignment
            </Typography>

            {/* Paging mode toggle */}
            <ToggleButtonGroup
              value={pagingMode}
              exclusive
              onChange={(_, v) => {
                if (!v) return;
                setPagingMode(v);
                setPerPage("");
                setMaxPages("");
              }}
              size="small"
              fullWidth
              sx={{ mb: 1 }}
            >
              <ToggleButton
                value="itemsPerPage"
                sx={{
                  fontFamily: "Poppins",
                  fontSize: 11,
                  textTransform: "none",
                }}
              >
                Items per page
              </ToggleButton>
              <ToggleButton
                value="maxPages"
                sx={{
                  fontFamily: "Poppins",
                  fontSize: 11,
                  textTransform: "none",
                }}
              >
                Max pages
              </ToggleButton>
            </ToggleButtonGroup>

            {/* Items per page input */}
            {pagingMode === "itemsPerPage" && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <TextField
                  size="small"
                  type="number"
                  inputProps={{ min: 1, style: { textAlign: "center" } }}
                  value={perPage}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (
                      v === "" ||
                      (Number(v) > 0 && Number.isInteger(Number(v)))
                    ) {
                      setPerPage(v);
                    }
                  }}
                  placeholder="—"
                  sx={{
                    width: 80,
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "var(--bg-paper)",
                      color: "var(--text-primary)",
                      borderRadius: "10px",
                      "& fieldset": {
                        borderColor: "primary.main",
                        borderWidth: 2,
                      },
                    },
                    "& .MuiInputBase-input": { color: "var(--text-primary)" },
                  }}
                />
                <Typography
                  fontFamily="Poppins"
                  fontSize={12}
                  color="var(--text-secondary)"
                >
                  products per page
                </Typography>
              </Box>
            )}

            {/* Max pages input */}
            {pagingMode === "maxPages" && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <TextField
                  size="small"
                  type="number"
                  inputProps={{ min: 1, style: { textAlign: "center" } }}
                  value={maxPages}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (
                      v === "" ||
                      (Number(v) > 0 && Number.isInteger(Number(v)))
                    ) {
                      setMaxPages(v);
                    }
                  }}
                  placeholder="—"
                  sx={{
                    width: 80,
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "var(--bg-paper)",
                      color: "var(--text-primary)",
                      borderRadius: "10px",
                      "& fieldset": {
                        borderColor: "primary.main",
                        borderWidth: 2,
                      },
                    },
                    "& .MuiInputBase-input": { color: "var(--text-primary)" },
                  }}
                />
                <Typography
                  fontFamily="Poppins"
                  fontSize={12}
                  color="var(--text-secondary)"
                >
                  max pages
                </Typography>
              </Box>
            )}

            {/* Auto-assigned preview */}
            {pagingMode === "itemsPerPage" && previewRows.length > 0 && (
              <Box
                sx={{
                  mt: 1.5,
                  p: 1.25,
                  borderRadius: "8px",
                  backgroundColor: "var(--bg-primary)",
                  border: "1px solid var(--border-color)",
                }}
              >
                <Typography
                  fontFamily="Poppins"
                  fontSize={11}
                  fontWeight={600}
                  color="var(--text-tertiary)"
                  sx={{
                    mb: 0.75,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  Auto-assigned preview
                </Typography>
                {previewRows.map((row) => (
                  <Box
                    key={row.page}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: 0.375,
                    }}
                  >
                    <Typography
                      fontFamily="Poppins"
                      fontSize={12}
                      color="var(--text-secondary)"
                    >
                      Products {row.from}–{row.to}
                    </Typography>
                    <Typography
                      fontFamily="Poppins"
                      fontSize={12}
                      fontWeight={500}
                      color="primary.main"
                    >
                      → Page {row.page}
                    </Typography>
                  </Box>
                ))}
                <Typography
                  fontFamily="Poppins"
                  fontSize={11}
                  color="var(--text-tertiary)"
                  sx={{ mt: 0.5, fontStyle: "italic" }}
                >
                  …and so on for remaining products
                </Typography>
              </Box>
            )}

            {pagingMode === "maxPages" && mp > 0 && (
              <Box
                sx={{
                  mt: 1.5,
                  p: 1.25,
                  borderRadius: "8px",
                  backgroundColor: "var(--bg-primary)",
                  border: "1px solid var(--border-color)",
                }}
              >
                <Typography
                  fontFamily="Poppins"
                  fontSize={11}
                  fontWeight={600}
                  color="var(--text-tertiary)"
                  sx={{
                    mb: 0.5,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  Auto-assigned preview
                </Typography>
                <Typography
                  fontFamily="Poppins"
                  fontSize={12}
                  color="var(--text-secondary)"
                >
                  All products will be evenly distributed across{" "}
                  <strong>{mp}</strong> page{mp !== 1 ? "s" : ""}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Import mode toggle — shown once an item is selected or attribute fields are filled */}
        {hasItems && (
          <Box sx={{ mt: 2.5 }}>
            <Typography
              fontFamily="Poppins"
              fontSize={12}
              fontWeight={500}
              color="var(--text-tertiary)"
              sx={{ mb: 0.75 }}
            >
              Import mode
            </Typography>
            <ToggleButtonGroup
              value={importMode}
              exclusive
              onChange={(_, v) => v && setImportMode(v)}
              size="small"
              fullWidth
            >
              <ToggleButton
                value="concat"
                sx={{
                  fontFamily: "Poppins",
                  fontSize: 12,
                  textTransform: "none",
                }}
              >
                Add to existing
              </ToggleButton>
              <ToggleButton
                value="replace"
                sx={{
                  fontFamily: "Poppins",
                  fontSize: 12,
                  textTransform: "none",
                }}
              >
                Replace current list
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}

        {/* Product Status Filter */}
        {hasItems && (
          <Box sx={{ mt: 2.5 }}>
            <Typography
              fontFamily="Poppins"
              fontSize={12}
              fontWeight={500}
              color="var(--text-tertiary)"
              sx={{ mb: 0.75 }}
            >
              Import status
            </Typography>
            <ToggleButtonGroup
              value={statusOption}
              exclusive
              onChange={(_, v) => v && setStatusOption(v)}
              size="small"
              fullWidth
            >
              <ToggleButton
                value="active"
                sx={{
                  fontFamily: "Poppins",
                  fontSize: 12,
                  textTransform: "none",
                }}
              >
                Active only
              </ToggleButton>
              <ToggleButton
                value="both"
                sx={{
                  fontFamily: "Poppins",
                  fontSize: 12,
                  textTransform: "none",
                }}
              >
                Active & Draft
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2, pt: 1 }}>
        <Button
          onClick={handleClose}
          size="small"
          sx={{ textTransform: "none", fontFamily: "Poppins" }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          disabled={
            filterBy === "attribute"
              ? !attributeType || !attributeValue.length || loading
              : !filterBy || !filterItem.length || loading
          }
          onClick={handleImport}
          sx={{ textTransform: "none", fontFamily: "Poppins" }}
        >
          {loading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
          Import
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportProductsModal;
